/**
 * WipVarianceMatrixPage
 *
 * "Scheduling vs. Timesheet Variance Matrix" page: summary metric cards,
 * filter bar, and the main interactive comparison grid with drill-down and
 * audit history actions. All variance numbers are computed client-side
 * (see useWipComparison.js) from SSS schedules + TRS time entries -- no
 * backend aggregates this data.
 *
 * Sample data is ONLY shown on a genuine fetch error (`isError`) -- not
 * merely because the real result came back empty. This cut-off period is
 * a narrow 2-week window, so a real, valid "no bookings/actuals yet for
 * this branch/period" result is entirely plausible and is real
 * information worth showing honestly, not something to paper over with
 * fictional sample rows. If TRS specifically fails while SSS is fine
 * (`isTrsError`), real SSS-driven rows still show with `actualHours: 0`
 * across the board, same graceful degradation the other pages use.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranchContext } from '../hooks/useBranchContext.js';
import { useComparisonRows, exportComparisonRowsToCsv, summarizeRows } from '../hooks/useWipComparison.js';
import { useEngagements, useStaffList } from '../hooks/useReferenceData.js';
import { sampleComparisonRows } from '../data/sampleVarianceMatrixData.js';
import { SummaryCards } from '../components/wip/SummaryCards.jsx';
import { WipFilters } from '../components/wip/WipFilters.jsx';
import { ComparisonGrid } from '../components/wip/ComparisonGrid.jsx';
import { AuditHistoryModal } from '../components/wip/AuditHistoryModal.jsx';

/**
 * @typedef {import('../hooks/useWipComparison.js').ComparisonRow} ComparisonRow
 */

export const WipVarianceMatrixPage = () => {
  const { activeBranchId, activeCutOff } = useBranchContext();
  const navigate = useNavigate();

  const [selectedStaffIds, setSelectedStaffIds] = useState(/** @type {string[]} */ ([]));
  const [selectedEngagementId, setSelectedEngagementId] = useState(
    /** @type {string|null} */ (null)
  );
  const [page, setPage] = useState(1);
  const [auditHistoryFor, setAuditHistoryFor] = useState(
    /** @type {ComparisonRow | null} */ (null)
  );

  const filters = useMemo(
    () => ({
      branchId: activeBranchId,
      cutOffPeriod: activeCutOff,
      staffIds: selectedStaffIds,
      engagementId: selectedEngagementId,
      page,
      perPage: 25,
    }),
    [activeBranchId, activeCutOff, selectedStaffIds, selectedEngagementId, page]
  );

  const { rows: fetchedRows, isLoading, isError, isTrsError } = useComparisonRows(filters);

  // Only a genuine SSS-side fetch error falls back to sample data --
  // empty (but successfully fetched) real rows are shown as-is, honestly.
  const usingSampleData = !isLoading && isError;
  const rows = usingSampleData ? sampleComparisonRows : fetchedRows;
  const summary = useMemo(() => summarizeRows(rows), [rows]);

  const branchFilter = activeBranchId ? { branch_id: activeBranchId } : {};
  const { data: engagementsData } = useEngagements(branchFilter);
  const { data: staffData } = useStaffList(branchFilter);

  const staffOptions = useMemo(
    () =>
      (staffData ?? []).map((s) => ({
        id: String(s.id),
        name: s.display_name ?? s.full_name ?? s.name ?? `Staff #${s.id}`,
      })),
    [staffData]
  );
  const engagementOptions = useMemo(
    () =>
      (engagementsData ?? []).map((e) => ({
        id: String(e.id),
        name: e.title ?? e.name ?? `Engagement #${e.id}`,
      })),
    [engagementsData]
  );

  const perPage = filters.perPage;
  const total = rows.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * perPage, page * perPage),
    [rows, page, perPage]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-primary">Scheduling vs. Timesheet Variance Matrix</h1>
          <p className="text-sm text-muted">
            Comparing SSS bookings against TRS actuals for the selected branch and cut-off period.
          </p>
        </div>
        {usingSampleData ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Sample data &mdash; live fetch failed
          </span>
        ) : isTrsError ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Live from SSS &middot; TRS unreachable, Actual Hrs showing 0
          </span>
        ) : (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            Live from SSS &amp; TRS
          </span>
        )}
      </div>

      <SummaryCards summary={summary} isLoading={isLoading} />

      <WipFilters
        staffOptions={staffOptions}
        selectedStaffIds={selectedStaffIds}
        onChangeStaffIds={(ids) => {
          setSelectedStaffIds(ids);
          setPage(1);
        }}
        engagementOptions={engagementOptions}
        selectedEngagementId={selectedEngagementId}
        onChangeEngagementId={(id) => {
          setSelectedEngagementId(id);
          setPage(1);
        }}
        onExport={() => exportComparisonRowsToCsv(rows, filters.cutOffPeriod)}
      />

      <ComparisonGrid
        rows={pageRows}
        isLoading={isLoading}
        onViewAuditHistory={(row) => setAuditHistoryFor(row)}
        onDrillDownTimesheet={(row) => navigate(`/timesheet/entries?engagement_id=${row.engagementId}&staff=${row.id}`)}
        onDrillDownSchedule={(row) => navigate(`/scheduling/bookings?engagement_id=${row.engagementId}&staff=${row.id}`)}
        onShowInEngagementView={(row) => navigate(`/engagements/${row.engagementId}`)}
      />

      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted">
          <span>
            Showing page {page} of {lastPage} ({total} rows)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-edge px-3 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-edge px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {auditHistoryFor && (
        <AuditHistoryModal
          engagementId={auditHistoryFor.engagementId}
          engagementName={auditHistoryFor.engagementName}
          onClose={() => setAuditHistoryFor(null)}
          onShowInEngagementView={() => {
            navigate(`/engagements/${auditHistoryFor.engagementId}`);
            setAuditHistoryFor(null);
          }}
        />
      )}
    </div>
  );
};
